import { AxiosRequestConfig, AxiosResponse } from 'axios';
import AbstractHttpService from '@services/abstractHttpService';

import { ACTION_REQUIRED_COUNTS_UPDATED_EVENT } from '@const/events';
import urls from '@const/urls';
import { IEnvelope, ICurrentEnvelope, IEnvelopesListParams, IDraftEnvelopeData, IEnvelopeParams, IEnvelopeDoHashData, IEnvelopeDoSignData } from '@store/modules/envelopes/types'
import { IParams } from '@app/types';
import { EnvelopeDirection, FileType, needResetParams } from '@app/const/consts';

export enum EnvelopeZipExportMode {
  BASE = 'BASE',
  EXTENDED = 'EXTENDED',
  FULL = 'FULL',
}

export enum EnvelopeZipExportNamePrefixMode {
  NONE = 'NONE',
  PLAIN_NUMBER = 'PLAIN_NUMBER',
  DATE_TIME = 'DATE_TIME',
}

export enum EnvelopeZipExportJobStatus {
  IN_QUEUE = 'IN_QUEUE',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
  STOPPED = 'STOPPED',
  EXPIRED = 'EXPIRED',
}

export enum EnvelopeExcelExportJobStatus {
  IN_QUEUE = 'IN_QUEUE',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
  STOPPED = 'STOPPED',
  EXPIRED = 'EXPIRED',
}

export interface IEnvelopeExcelExportJobPostRequest {
  previousJobOguid?: string | null;
  envelopeOguids?: string[] | null;
  envelopeLimit?: number | null;
}

export interface IEnvelopeExcelExportJobResponse {
  oguid: string;
  status: EnvelopeExcelExportJobStatus;
  direction: EnvelopeDirection;
  envelopeLimit: number;
  exportedEnvelopeCount: number | null;
  hasNext: boolean | null;
  errorCode: string | null;
  errorDescription: string | null;
  progressStage: string | null;
  processedRowCount: number | null;
  totalChunkCount: number | null;
  time: number | null;
  fileSizeBytes: number | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  downloadExpiresAt: string | null;
  fileStoredUntil: string | null;
  fileRemovedAt: string | null;
}

export interface IEnvelopeZipExportJobPostRequest {
  mode?: EnvelopeZipExportMode;
  nameRulesTemplate?: string;
  namePrefixMode?: EnvelopeZipExportNamePrefixMode;
  envelopeLimit?: number | null;
  lastKey?: number | null;
  previousJobOguid?: string | null;
  envelopeOguids?: string[] | null;
}

export interface IEnvelopeZipExportJobResponse {
  oguid: string;
  status: EnvelopeZipExportJobStatus;
  mode: EnvelopeZipExportMode;
  namePrefixMode: EnvelopeZipExportNamePrefixMode;
  envelopeLimit: number;
  exportedEnvelopeCount: number | null;
  lastKey: number | null;
  hasNext: boolean | null;
  errorCode: string | null;
  errorDescription: string | null;
  progressStage: string | null;
  time: number | null;
  fileCount: number | null;
  processedFileCount: number | null;
  fileSizeBytes: number | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  downloadExpiresAt: string | null;
  fileStoredUntil: string | null;
  fileRemovedAt: string | null;
}

export interface IActionRequiredEnvelopeBoxCounts {
  boxOguid: string;
  actionRequiredTaskCount: number;
}

export interface IActionRequiredEnvelopeCounts {
  actionRequiredTaskCount: number;
  boxes: IActionRequiredEnvelopeBoxCounts[] | null;
}

const normalizeEnvelopeListItem = (envelope: any): IEnvelope => ({
  ...envelope,
  direction: envelope.direction ?? envelope.directions?.[0] ?? null,
  directions: envelope.directions ?? (envelope.direction ? [envelope.direction] : null),
  labels: envelope.labels ?? [],
  recipients: envelope.recipients?.map((recipient: any) => ({
    ...recipient,
    assignedToBoxUser: recipient.assignedToBoxUser ?? null,
    email: recipient.email ?? null,
    fullName: recipient.fullName ?? recipient.assignedToBoxUser?.fullName ?? null,
    guestEmail: recipient.guestEmail ?? null,
  })) ?? [],
});

const normalizeNotifications = (notifications: any) => notifications
  ? {
      subscriptions: notifications.subscriptions?.map((subscription: any) => {
        return {
          events: subscription.events ?? null,
          notifyOnFinished: subscription.notifyOnFinished ?? false,
          subscriber: {
            ...subscription.subscriber,
            boxUserOguid: subscription.subscriber.boxUserOguid ?? null,
            recipientKey: subscription.subscriber.recipientKey ?? null,
          },
        }
      }) ?? null,
    }
  : null;

const normalizeCurrentEnvelope = (envelope: any): ICurrentEnvelope => ({
  ...envelope,
  notifications: normalizeNotifications(envelope.notifications),
  recipients: normalizeEnvelopeListItem(envelope).recipients,
  flow: envelope.flow?.map((stage: any) => ({
    ...stage,
    description: stage.description ?? null,
    stageKey: stage.stageKey ?? null,
    labels: stage.labels ?? [],
    tasks: stage.tasks?.map((task: any) => {
      const assignedToBoxUser = task.recipient?.assignedToBoxUser ?? null;

      return {
        ...task,
        recipient: task.recipient
          ? {
              ...task.recipient,
              assignedToBoxUser,
              boxOguid: task.recipient.boxOguid ?? null,
              declaredSigner: task.recipient.declaredSigner ?? null,
              email: task.recipient.email ?? null,
              guestEmail: task.recipient.guestEmail ?? null,
              isAssignedToBoxUser: Boolean(assignedToBoxUser),
              isHostMode: task.recipient.isHostMode ?? false,
              recipientKey: task.recipient.recipientKey ?? null,
            }
          : task.recipient,
      };
    }) ?? [],
  })) ?? [],
});

class EnvelopesService extends AbstractHttpService {
  private readonly url = urls.envelopes

  async getEnvelopesList ({ direction, boxOguid, page, perPage, otherSearchParams }: IEnvelopesListParams): Promise<AxiosResponse<IEnvelope[]>> {
    const url = this.url.list

    let params : IParams = {}

    direction && (params['direction'] = direction)
    boxOguid && (params['boxOguid'] = boxOguid)
    page && (params['page'] = page)
    perPage && (params['perPage'] = perPage)

    if (otherSearchParams) {
      params={
        ...params,
        ...otherSearchParams
      }
    }

    const config: AxiosRequestConfig = {
      params
    }

    const response = await this._http.get(url, config)

    return {
      ...response,
      data: response.data.map(normalizeEnvelopeListItem),
    }
  }

  async getActionRequiredCounts(params: IParams = {}): Promise<AxiosResponse<IActionRequiredEnvelopeCounts>> {
    const url = this.url.actionRequiredCounts

    return await this._http.get(url, { params })
  }

  async sendEnvelope (data: IDraftEnvelopeData): Promise<AxiosResponse<string>> {
    const url = this.url.list
    const config: AxiosRequestConfig = {
      params: needResetParams
    }

    return await this._http.post(url, data, config)
  }

  async getEnvelope ({ direction, oguid, boxOguid }: IEnvelopeParams): Promise<AxiosResponse<ICurrentEnvelope>> {
    const url = this.url.envelope.replace('{{ envelopeOguid }}', oguid).replace('{{ boxOguid }}', boxOguid)

    const response = await this._http.get(url, {
      params: direction ? { direction } : undefined,
    })

    return {
      ...response,
      data: normalizeCurrentEnvelope(response.data),
    }
  }

  async getEnvelopeCommentFile (envelopeOguid: string, commentFileOguid: string): Promise<AxiosResponse<Blob>> {
    const url = this.url.commentFile
      .replace('{{ envelopeOguid }}', envelopeOguid)
      .replace('{{ commentFileOguid }}', commentFileOguid)

    return await this._http.get(url, { responseType: 'blob' })
  }

  async getEnvelopeFile (
    envelopeOguid: string,
    fileIndex: number,
    fileType: `${FileType}.PDF`,
    isNeedResetError = false
  ): Promise<AxiosResponse<ArrayBuffer>> {
    const url = this.url.file
      .replace('{{ envelopeOguid }}', envelopeOguid)
      .replace('{{ fileIndex }}', String(fileIndex))
      .replace('{{ fileType }}', fileType)

    const config: AxiosRequestConfig = {
      responseType: 'arraybuffer'
    }

    if (isNeedResetError) {
      config.params = needResetParams
    }

    return await this._http.get(url, config)
  }

  async postEnvelopeGenerateShare (envelopeOguid: string): Promise<AxiosResponse<string>> {
    const url = this.url.generateShare.replace('{{ envelopeOguid }}', envelopeOguid)

    return await this._http.post(url, {})
  }

  async postEnvelopeFlowStop (oguid: string): Promise<AxiosResponse> {
    const url = this.url.flowStop.replace('{{ envelopeOguid }}', oguid)

    return await this._http.post(url, {})
  }

  // https://api-gd-backend-dev.dev.info-logistics.eu/specification/redoc.html#tag/envelopes/operation/postHashPdf
  async postEnvelopeDoHash (oguid: string, fileIndex: string, data: IEnvelopeDoHashData): Promise<AxiosResponse<string>> {
    const url = this.url.signing.doHash.replace('{{ envelopeOguid }}', oguid).replace('{{ fileIndex }}', fileIndex)

    const params = needResetParams

    const config: AxiosRequestConfig = {
      params
    }

    return await this._http.post(url, data, config)
  }

  // https://api-gd-backend-dev.dev.info-logistics.eu/specification/redoc.html#tag/envelopes/operation/postSignPdf
  async postEnvelopeDoSign (oguid: string, data: IEnvelopeDoSignData): Promise<AxiosResponse<string[]>> {
    const url = this.url.signing.doSign.replace('{{ envelopeOguid }}', oguid)
    const response = await this._http.post(url, data)

    window.dispatchEvent(new Event(ACTION_REQUIRED_COUNTS_UPDATED_EVENT))

    return response
  }

  async postExportExcelJob (
    data: IEnvelopeExcelExportJobPostRequest,
    params: IParams = {},
    formatLocale?: string
  ): Promise<AxiosResponse<IEnvelopeExcelExportJobResponse>> {
    const url = this.url.exportExcelJobs
    const config: AxiosRequestConfig = {
      params: {
        ...params,
        ...needResetParams,
      },
      ...(formatLocale ? { headers: { 'X-Format-Locale': formatLocale } } : {}),
    }

    return await this._http.post(url, data, config)
  }

  async getExportExcelJob (exportExcelJobOguid: string): Promise<AxiosResponse<IEnvelopeExcelExportJobResponse>> {
    const url = this.url.exportExcelJob.replace('{{ exportExcelJobOguid }}', exportExcelJobOguid)

    return await this._http.get(url, { params: needResetParams })
  }

  async getExportExcelJobFile (exportExcelJobOguid: string): Promise<AxiosResponse<Blob>> {
    const url = this.url.exportExcelJobFile.replace('{{ exportExcelJobOguid }}', exportExcelJobOguid)

    return await this._http.get(url, { params: needResetParams, responseType: 'blob' })
  }

  async postExportZipJob (
    data: IEnvelopeZipExportJobPostRequest,
    params: IParams = {},
    formatLocale?: string
  ): Promise<AxiosResponse<IEnvelopeZipExportJobResponse>> {
    const url = this.url.exportZipJobs

    const config: AxiosRequestConfig = {
      params: {
        ...params,
        ...needResetParams,
      },
      ...(formatLocale ? { headers: { 'X-Format-Locale': formatLocale } } : {}),
    }

    return await this._http.post(url, data, config)
  }

  async getExportZipJob (exportZipJobOguid: string): Promise<AxiosResponse<IEnvelopeZipExportJobResponse>> {
    const url = this.url.exportZipJob.replace('{{ exportZipJobOguid }}', exportZipJobOguid)

    return await this._http.get(url, { params: needResetParams })
  }

  async getExportZipJobFile (exportZipJobOguid: string): Promise<AxiosResponse<Blob>> {
    const url = this.url.exportZipJobFile.replace('{{ exportZipJobOguid }}', exportZipJobOguid)

    return await this._http.get(url, { params: needResetParams, responseType: 'blob' })
  }
}

export default new EnvelopesService()
